import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Input,
  Label,
  TextField,
} from "react-aria-components";
import { Save } from "lucide-react";
import { getCsrf } from "../api";

type MailboxDelay = {
  id: string;
  email: string;
  cooldownMinSeconds: number;
  cooldownMaxSeconds: number;
};

export function SendingDelaySettings({
  projectId,
  accounts,
  onChange,
}: {
  projectId: string;
  accounts: MailboxDelay[];
  onChange: () => void;
}) {
  const [minimum, setMinimum] = useState("190");
  const [maximum, setMaximum] = useState("370");
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(accounts.map((account) => account.id)),
  );
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    setSelected(new Set(accounts.map((account) => account.id)));
    if (accounts[0]) {
      setMinimum(String(accounts[0].cooldownMinSeconds || 190));
      setMaximum(String(accounts[0].cooldownMaxSeconds || 370));
    }
  }, [accounts]);

  const error = useMemo(() => {
    const min = Number(minimum);
    const max = Number(maximum);
    if (!Number.isInteger(min) || !Number.isInteger(max))
      return "Только секунды.";
    if (min < 10 || max > 86400) return "Диапазон: 10–86400.";
    if (max < min) return "Максимум меньше минимума.";
    if (!selected.size) return "Выберите ящики.";
    return "";
  }, [maximum, minimum, selected]);

  const save = async () => {
    if (error || busy) return;
    const csrf = await getCsrf();
    if (!csrf) {
      setNotice("Войдите заново.");
      return;
    }
    setBusy(true);
    setNotice("");
    try {
      const response = await fetch(
        `/api/projects/${projectId}/mailboxes/sending-settings`,
        {
          method: "PATCH",
          headers: {
            "content-type": "application/json",
            "x-csrf-token": csrf,
          },
          body: JSON.stringify({
            mailboxIds: [...selected],
            cooldownMinSeconds: Number(minimum),
            cooldownMaxSeconds: Number(maximum),
          }),
        },
      );
      const result = (await response.json()) as {
        updated?: number;
        error?: string;
      };
      if (!response.ok) throw new Error(result.error ?? "Не сохранено");
      setNotice(`Сохранено: ${result.updated ?? selected.size}`);
      onChange();
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "Ошибка");
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="section-card sending-delay-settings">
      <header>
        <div>
          <h2>Дилей отправки</h2>
          <p>Случайно между границами.</p>
        </div>
        <span>Coldy + 10 сек</span>
      </header>
      <div className="sending-delay-range">
        <TextField value={minimum} onChange={setMinimum} isDisabled={busy}>
          <Label>От, сек</Label>
          <Input type="number" min={10} max={86400} inputMode="numeric" />
        </TextField>
        <TextField value={maximum} onChange={setMaximum} isDisabled={busy}>
          <Label>До, сек</Label>
          <Input type="number" min={10} max={86400} inputMode="numeric" />
        </TextField>
      </div>
      <div className="sending-delay-targets" aria-label="Ящики с дилеем">
        {accounts.map((account) => (
          <Checkbox
            key={account.id}
            isSelected={selected.has(account.id)}
            isDisabled={busy}
            onChange={(checked) => {
              setSelected((current) => {
                const next = new Set(current);
                if (checked) next.add(account.id);
                else next.delete(account.id);
                return next;
              });
            }}
          >
            <span aria-hidden="true" />
            {account.email}
          </Checkbox>
        ))}
      </div>
      <footer>
        <span className={error ? "error" : ""}>{error || notice}</span>
        <Button
          className="primary-button"
          onPress={() => void save()}
          isDisabled={busy || Boolean(error)}
        >
          <Save size={14} /> Сохранить
        </Button>
      </footer>
    </article>
  );
}
